<template>
  <Loading v-if="!didLoadData" />
  <v-container v-else-if="!hasFacultyAccess || !singleDetail">
    <v-row>
      <v-col>
        <BackButton :target="{name: 'Newsfeed'}">{{
          $t("news.news_list")
        }}</BackButton>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <PlaceHolder
          :path="'/icons/something-went-wrong.png'"
          :text="$t('news.something_went_wrong_text')"
          :title="$t('news.something_went_wrong_title')"
        >
        </PlaceHolder>
      </v-col>
    </v-row>
  </v-container>
  <v-container v-else>
    <v-dialog
      v-model="showNotificationDialog"
      width="500"
      @keydown.esc="() => (showNotificationDialog = false)"
      @click:outside="() => (showNotificationDialog = false)"
    >
      <v-card>
        <v-row justify="end">
          <v-col
            @click="() => (showNotificationDialog = false)"
            class="pt-6 pr-6"
            cols="auto"
          >
            <v-icon style="font-size: 1.9em; cursor: pointer"
              >highlight_off</v-icon
            >
          </v-col>
        </v-row>
        <v-card-title class="pt-0">
          <span class="text-h5">{{
            $t("news.notification_dialog_title")
          }}</span>
        </v-card-title>

        <v-card-text id="dialog-text">
          {{ $t("news.notification_dialog_description") }}
          <br />
          <v-img
            src="@/assets/warning-notification.jpeg"
            style="width: 100%; margin-top: 1rem"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            data-cy="dialog-yes-btn"
            color="grey"
            text
            @click="onConfirmSave(true)"
          >
            {{ $t("entities.alert") }}
          </v-btn>
          <v-btn color="green" text @click="onConfirmSave(false)">
            {{ $t("entities.not_alert") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <Alert :model="getNewsfeedDetailError" :onConfirm="() => setErr(false)">
      {{ $t("news.something_went_wrong") }}
    </Alert>
    <v-row>
      <v-col class="py-0">
        <BackButton :target="{name: 'Newsfeed'}">{{
          $t("news.news_list")
        }}</BackButton>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="8">
        <v-container fluid>
          <v-row align="center">
            <div
              class="text-h4 font-weight-medium mr-4"
              data-cy="newsfeed-detail-heading"
            >
              {{ isNew ? $t("news.new_post") : title }}
            </div>

            <v-chip v-if="singleDetail.isArchived" size="small">
              <v-icon class="mr-1" small>inventory_2</v-icon>
              <span>{{ $t("news.archived") }}</span>
            </v-chip>

            <v-chip
              variant="elevated"
              color="primary"
              v-else-if="!singleDetail.isConcept"
              size="small"
            >
              <v-icon style="color: #eeeeef" class="mr-1" size="small"
                >check_circle</v-icon
              >
              <span style="color: #eeeeef; font-weight: 500">{{
                $t("news.published")
              }}</span>
            </v-chip>

            <v-chip v-else size="small">
              <v-icon class="mr-1" size="small">edit</v-icon>
              <span>{{ $t("news.concept") }}</span>
            </v-chip>
          </v-row>
        </v-container>
      </v-col>
      <v-col
        align-self="center"
        class="d-flex justify-md-end justify-start"
        cols="12"
        md="4"
        v-if="this.singleDetail.isConcept"
      >
        <v-btn
          v-if="!savedConcept"
          :disabled="isFormInvalid"
          color="light-green darken-1"
          variant="outlined"
          rounded
          style="border-width: 2px"
          @click="savePost('concept')"
          data-cy="save-concept"
        >
          {{ $t("news.save_concept") }}
        </v-btn>
        <v-btn v-else variant="outlined">
          <v-icon left>check</v-icon>
          {{ $t("news.saved") }}
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="7">
        <v-tabs v-model="tab" color="light-green" icons-and-text stacked>
          <v-tab value="Text">
            <v-icon>question_answer</v-icon>
            {{ $t("news.textual") }}
          </v-tab>
          <v-tab value="Poster">
            <v-icon>image</v-icon>
            {{ $t("news.poster") }}
          </v-tab>
        </v-tabs>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12">
        <v-alert
          v-if="isFormInvalid"
          text
          variant="outlined"
          shaped
          type="warning"
          class="mt-4 mb-0"
        >
          <span>{{ $t("news.post_hint") }}</span>
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-container fluid>
        <v-row>
          <v-col cols="12">
            <v-row>
              <v-col>
                <h2 class="font-weight-medium">{{ $t("news.post") }}</h2>
              </v-col>
            </v-row>
            <v-container fluid>
              <v-row>
                <v-col class="px-0" cols="12">
                  <v-row justify="space-between">
                    <v-col cols="6">
                      <h3>{{ $t("entities.czech") }}</h3>
                    </v-col>
                    <v-col cols="6">
                      <h3>{{ $t("entities.english") }}</h3>
                    </v-col>
                  </v-row>
                  <v-row justify="space-between">
                    <v-col cols="6">
                      <v-textarea
                        variant="underlined"
                        data-cy="title-cz"
                        :label="$t('news.news_title')"
                        :rules="[required_rule]"
                        v-model.trim="singleDetail.localizedAttributes.cz.title"
                        :rows="1"
                        :maxlength="100"
                        counter
                        auto-grow
                      >
                      </v-textarea>
                    </v-col>
                    <v-col cols="6">
                      <v-textarea
                        variant="underlined"
                        data-cy="title-en"
                        label="News title"
                        :rules="[required_rule]"
                        v-model.trim="singleDetail.localizedAttributes.en.title"
                        :rows="1"
                        :maxlength="100"
                        counter
                        auto-grow
                      >
                      </v-textarea>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-if="this.isTextual">
                <v-col cols="12" class="pa-0">
                  <MarkDownEditor
                    v-if="markdownTextLoaded"
                    :text-cz="markdownedTextCz"
                    :text-en="markdownedTextEn"
                    @update:cz="changeCzText"
                    @update:en="changeEnText"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col class="pa-0 mt-2">
                  <h2 class="font-weight-medium">
                    {{ $t("news.additional_info") }}
                  </h2>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="pa-0 mt-2">
                  <v-alert
                    variant="outlined"
                    shaped
                    type="warning"
                    class="mt-4 mb-0"
                  >
                    <span>{{ $t("news.detail_hint") }}</span>
                  </v-alert>
                </v-col>
              </v-row>
              <v-row justify="space-between">
                <v-col cols="12" md="6" class="pl-0">
                  <v-combobox
                    variant="underlined"
                    data-cy="place"
                    v-model="place"
                    @update:search="s => placeSearch = s"
                    :items="getNewsfeedPlaces"
                    :label="$t('news.place')"
                    color="light-green darken-1"
                    prepend-icon="location_on"
                    clearable
                    return-object
                  ></v-combobox>
                </v-col>
                <v-col v-if="isCustomPlace" cols="12" md="6" class="pr-0">
                  <v-text-field
                    variant="underlined"
                    v-model="placeEN"
                    :label="$t('news.placeEN')"
                    color="light-green darken-1"
                    prepend-icon="location_on"
                    clearable
                    :rules="[required_rule]"
                    data-cy="place-en"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row class="align-center">
                <v-col cols="12" md="6" class="pl-0">
                  <DatePicker
                    variant="underlined"
                    data-cy="event-date"
                    :initial-value="this.singleDetail.eventDate"
                    :clearable="true"
                    :minDate="currentDate"
                    :title="$t('news.date')"
                    v-on:dateChanged="(value) => updateDate(value)"
                  />
                </v-col>
                <v-col cols="12" md="5" class="pr-0">
                  <time-picker
                    variant="underlined"
                    :title="$t('news.time')"
                    v-model="singleDetail.eventTime"
                    :disabled="!this.singleDetail.eventDate"
                    :clearable="true"
                    @update:valid="v => eventTimeValid = v"
                  >
                  </time-picker>
                </v-col>
              </v-row>
            </v-container>
          </v-col>

          <v-col v-if="postUploading" cols="12">
            <v-container fluid>
              <v-row class="align-center">
                <v-col cols="auto">
                  <v-dialog
                    class="rounded-lg"
                    v-model="postUploading"
                    max-width="500px"
                    persistent
                  >
                    <v-card class="rounded-lg pa-6">
                      <v-row justify="space-between" class="align-center">
                        <v-col cols="auto" class="pa-0">
                          <v-card-title class="pl-1 pt-0">
                            <span>{{ $t("news.uploading") }}</span>
                          </v-card-title>
                        </v-col>
                      </v-row>
                      <v-row justify="center" class="align-center">
                        <v-col cols="auto" class="py-0">
                          <v-progress-circular
                            indeterminate
                          ></v-progress-circular>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="auto" class="pb-0">
                          <v-alert
                            type="info"
                            transition="fade-transition"
                            color="#555"
                            variant="outlined"
                            class="rounded-lg pt-2 pb-1 px-3"
                          >
                            {{ $t("news.info_images_upload") }}
                          </v-alert>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-dialog>
                </v-col>
              </v-row>
            </v-container>
          </v-col>
        </v-row>
      </v-container>
    </v-row>

    <v-row>
      <v-col cols="auto">
        <h2 class="font-weight-medium">{{ $t("news.images_title") }}</h2>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="6">
        <h3 v-if="!isTextual" class="font-weight-medium">
          {{ $t("news.poster") }}
        </h3>
        <h3 v-else class="font-weight-medium">
          {{ $t("news.title_image") }}
        </h3>
        <file-manager
          ref="titleImageManager"
          :files="titleImages"
          @update:files="setTitleImages"
          preview="gallery"
          upload-attribute="files"
          :file-url="file => `newsfeed/news/images/${newsId}/${file.id}`"
          :delete-url="file => `newsfeed/news/images/${newsId}/${file.id}`"
          :multiple="false"
          :disabled="titleImages.length > 0"
        />
      </v-col>
      <v-col cols="12" md="6" v-if="isTextual">
        <h3 class="font-weight-medium">
          {{ $t("news.additional_images") }}
        </h3>
        <file-manager
          ref="additionalImagesManager"
          :files="additionalImages"
          @update:files="setAdditionalImages"
          preview="gallery"
          upload-attribute="files"
          :file-url="file => `newsfeed/news/images/${newsId}/${file.id}`"
          :delete-url="file => `newsfeed/news/images/${newsId}/${file.id}`"
          :multiple="true"
        />
      </v-col>
    </v-row>
        <v-alert
          variant="outlined"
          type="info"
          color="#555"
          class="mt-4 mb-0 rounded-lg"
          >{{ $t("news.info_images") }}</v-alert
        >

        <h2 class="font-weight-medium pt-7">{{ $t("news.further_information") }}</h2>

    <v-row justify="end">
      <v-col align-self="center" cols="12" md="8">
        <h3 class="font-weight-medium">{{ $t("news.links") }}</h3>
      </v-col>
      <v-col
        align-self="center"
        class="d-flex justify-md-end justify-start"
        cols="12"
        md="4"
      >
        <v-btn
          color="primary"
          dark
          @click="openDialog('link')"
          data-cy="btn-add-link"
        >
          {{ $t("news.add_link") }}
        </v-btn>
        <v-dialog v-model="showDialogLink" max-width="600px" persistent>
          <NewsfeedDialog
            :status="isNewCard"
            :textDetail="itemCard"
            type="link"
            :label="$t('news.name_link')"
            :onClose="() => closeDialog('link')"
            :onSave="
              (text, index, status) => saveCard(text, index, status, 'link')
            "
            :titleNew="$t('news.add_link')"
            :titleEdit="$t('news.edit_link')"
          />
        </v-dialog>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <NewsfeedCards
          data-cy="card-links"
          type="odkaz"
          :input="this.singleDetail.links"
          @deleteCard="removeItem"
          @editCard="(text, index) => prepareEditCard(text, index, 'link')"
        />
      </v-col>
    </v-row>

    <v-row justify="end">
      <v-col align-self="center" cols="12" md="8">
        <h3 class="font-weight-medium">{{ $t("news.contact_data") }}</h3>
      </v-col>
      <v-col
        align-self="center"
        class="d-flex justify-md-end justify-start"
        cols="12"
        md="4"
      >
        <v-btn
          color="primary"
          dark
          @click="openDialog('contact')"
          data-cy="btn-add-contact"
        >
          {{ $t("news.add_contact") }}
        </v-btn>
        <v-dialog v-model="showDialogContact" max-width="600px" persistent>
          <NewsfeedDialog
            :status="isNewCard"
            :textDetail="itemCard"
            type="contact"
            :label="$t('news.name_contact')"
            :onClose="() => closeDialog('contact')"
            :onSave="
              (text, index, status) => saveCard(text, index, status, 'contact')
            "
            :titleNew="$t('news.add_contact')"
            :titleEdit="$t('news.edit_contact')"
          />
        </v-dialog>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <NewsfeedCards
          data-cy="card-contacts"
          type="kontakt"
          :input="this.singleDetail.contacts"
          @deleteCard="removeItem"
          @editCard="(text, index) => prepareEditCard(text, index, 'contact')"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <h2 class="font-weight-medium">{{ $t("news.post_targeting") }}</h2>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <h3 class="font-weight-medium">{{ $t("news.post_topic") }}</h3>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <NewsCards
          data-cy="card-category"
          :input="getCategoriesNew"
          data="category"
          @clicked="onClickChild"
          :selectedItems="selectedCategories"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <h3 class="font-weight-medium">{{ $t("news.target_groups") }}</h3>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <NewsCards
          data-cy="card-audience"
          :input="getAudiencesByUser"
          data="audience"
          @clicked="onClickChild"
          :selectedItems="selectedAudiences"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <h3 class="font-weight-medium">{{ $t("news.target_parts") }}</h3>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <NewsCards
          data-cy="card-faculty"
          :input="getFacultiesByUser"
          data="faculty"
          @clicked="onClickChild"
          :selectedItems="selectedFaculties"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-container class="footerBackground pa-8">
          <v-row>
            <v-col>
              <h2 class="font-weight-medium">{{ $t("news.publication") }}</h2>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <h4 class="font-weight-medium footerText">
                {{ $t("news.time_range_publication") }}
              </h4>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="4" class="px-3 py-0">
              <DatePicker
                variant="underlined"
                data-cy="date-from"
                :initial-value="
                  this.singleDetail.startDate
                    ? this.singleDetail.startDate
                    : null
                "
                :clearable="true"
                :minDate="currentDate"
                :title="$t('news.date_from')"
                v-on:dateChanged="
                  (value) => {
                    this.singleDetail.startDate = value
                      ? value.format('YYYY-MM-DD')
                      : undefined;
                  }
                "
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="4" class="px-3 py-0">
              <DatePicker
                variant="underlined"
                data-cy="date-to"
                :initial-value="
                  this.singleDetail.endDate ? this.singleDetail.endDate : null
                "
                :clearable="true"
                :minDate="
                  this.singleDetail.startDate
                    ? this.singleDetail.startDate
                    : currentDate
                "
                :title="$t('news.date_to')"
                v-on:dateChanged="
                  (value) => {
                    this.singleDetail.endDate = value
                      ? value.format('YYYY-MM-DD')
                      : undefined;
                  }
                "
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-btn
                style="background-color: #78be13; color: white"
                :disabled="isFormInvalid"
                large
                @click="() => askAboutNotification()"
                data-cy="publish"
              >
                <span v-if="singleDetail.isConcept || singleDetail.isArchived">
                  {{ $t("news.publish_post") }}
                </span>
                <span v-else> {{ $t("news.update_post") }}</span>
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <span
                v-if="this.singleDetail.isArchived"
                class="footerTextArchived"
              >
                {{ $t("news.post_is_archived") }}
              </span>
              <span v-else-if="this.singleDetail.isConcept" class="footerText">
                {{ $t("news.post_is_not_published") }}
              </span>
              <span v-else class="footerTextSuccess">
                {{ $t("news.post_is_published") }}
              </span>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <span
                v-if="!savedArchived && !singleDetail.isArchived"
                data-cy="archive"
                :class="setButtonClass"
                @click="
                  () => {
                    if (!isFormInvalid) savePost('archive');
                  }
                "
              >
                {{ $t("news.archive_post") }}
              </span>
              <v-btn v-else-if="savedArchived" variant="outlined">
                <v-icon left>check</v-icon>
                {{ $t("news.archived_post") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import BackButton from "@/components/layout/BackButton";
import Loading from "@/components/Loading";
import {getLocalizedLanguages} from "@/localization/languages";
import moment from "moment";
import DatePicker from "@/components/date-picker/DatePicker";
import NewsfeedCards from "@/components/news/NewsfeedCards";
import {mapActions, mapGetters, mapMutations} from "vuex";
import NewsCards from "@/components/news/NewsCards";
import NewsfeedDialog from "@/components/news/NewsfeedDialog";
import Alert from "@/components/dialogs/Alert";
import PlaceHolder from "@/components/news/PlaceHolder";
import MarkDownEditor from "@/components/MarkDownEditor.vue";
import TimePicker from "../../components/time-picker/TimePicker.vue";
import MarkdownIt from "markdown-it";
import FileManager from "@/components/files/FileManager.vue";

export default {
  name: "NewsfeedDetail",

  components: {
    FileManager,
    DatePicker,
    BackButton,
    NewsfeedCards,
    Loading,
    NewsCards,
    NewsfeedDialog,
    Alert,
    PlaceHolder,
    MarkDownEditor,
    TimePicker,
  },

  async beforeMount() {
    this.isNew ? this.preparePost() : await this.fetchNewsById(this.newsId);
    this.loadPostStatus();
  },

  async created() {
    await Promise.all([
      await this.fetchAllFacultiesByUser(),
      await this.fetchAllCategories(),
      await this.fetchAllAudiencesByUser(),
      await this.fetchNewsfeedImages(this.$route.params.idNews),
    ]);
    this.controlFacultyAccess();
    this.fetchData();
    window.scrollTo(0, 0);
    this.tab = this.singleDetail.isPoster ? "Poster" : "Text";

    //assign the localized text to the markdownedText
    this.markdownedTextCz = this.singleDetail.localizedAttributes.cz.text
      ? this.singleDetail.localizedAttributes.cz.text
      : "";
    this.markdownedTextEn = this.singleDetail.localizedAttributes.en.text
      ? this.singleDetail.localizedAttributes.en.text
      : "";
    this.markdownTextLoaded = true;

    this.setPlaceAfterFetch()

  },

  data() {
    return {
      titleFiles: [],
      allowedFileTypes: [".jpg", ".jpeg", ".png"],
      maxFileSize: 15,
      showNotificationDialog: false,
      tab: null,
      currentLanguage: "cz",
      showDialogLink: false,
      showDialogContact: false,
      published: false,
      required_rule: (value) => !!value || "Povinné",
      savedConcept: false,
      savedArchived: false,
      errorOnCreate: false,
      hasFacultyAccess: true,
      facultyIds: [],
      audienceIds: [],
      categoryIds: [],
      uploadingTitleImagePercentage: 0,
      uploadingAdditionalImagesPercentage: 0,
      place: null,
      placeSearch: "",
      placeEN: null,
      itemCard: {},
      isNewCard: null,
      title: null,
      postUploading: false,
      markdownedTextCz: "",
      markdownedTextEn: "",
      markdownTextLoaded: false,
      eventTimeValid: false,
    };
  },

  computed: {
    ...mapGetters("Newsfeed", [
      "getNewsfeedDetailError",
      "getCategoriesNew",
      "getAudiencesByUser",
      "getFacultiesByUser",
      "getEmptyPost",
      "didLoadCategories",
      "didLoadFaculties",
      "didLoadAudiences",
      "getNewsById",
      "getNewsfeedImages",
    ]),
    ...mapGetters("places", ["getPlaces"]),

    didLoadData() {
      return (
        this.didLoadFaculties && this.didLoadCategories && this.didLoadAudiences
      );
    },

    singleDetail() {
      return !this.isNew ? this.getNewsById(this.newsId) : this.getEmptyPost;
    },

    newsId() {
      return parseInt(this.$route.params.idNews);
    },

    facultyId() {
      return parseInt(this.$route.params.id);
    },

    isCustomPlace() {
        // if you picked from list, the place is object
        if(typeof this.place !== "object" && this.place){
          return true
        } else {
          return false
        }
    },

    languages() {
      const langs = getLocalizedLanguages();
      return Object.keys(langs).map((lng) => ({
        title: langs[lng],
        value: lng,
      }));
    },

    currentDate() {
      return moment(Date.now()).format("YYYY-MM-DD");
    },

    isFormInvalid() {
      let rules =
        this.facultyIds.length === 0 ||
        this.audienceIds.length === 0 ||
        this.categoryIds.length === 0 ||
        !this.singleDetail.localizedAttributes.cz.title ||
        !this.singleDetail.localizedAttributes.en.title ||
        !this.eventTimeValid;

      if (!this.isPlaceFromList()) {
        rules =
          this.facultyIds.length === 0 ||
          this.audienceIds.length === 0 ||
          this.categoryIds.length === 0 ||
          !this.singleDetail.localizedAttributes.cz.title ||
          !this.singleDetail.localizedAttributes.en.title ||
          !this.eventTimeValid ||
          !this.placeEN;
      }

      return this.tab === "Poster"
        ? rules
        : rules ||
            !this.singleDetail.localizedAttributes.cz.text ||
            !this.singleDetail.localizedAttributes.en.text;
    },

    isTextual() {
      return this.tab !== "Poster";
    },

    selectedFaculties() {
      return !this.isNew ? this.singleDetail.faculty : [{id: this.facultyId}];
    },

    selectedAudiences() {
      return !this.isNew ? this.singleDetail.audience : [];
    },

    selectedCategories() {
      return !this.isNew ? this.singleDetail.category : [];
    },

    isNew() {
      return this.$route.params.idNews === "new";
    },

    getNewsfeedPlaces() {
      const getPlaceItems = [];
      this.getPlaces.map((place) => {
        if (place.localizedAttributes.cz.name) {
          getPlaceItems.push({
            id: place.id,
            title: place.localizedAttributes.cz.name,
          });
        }
      });
      return getPlaceItems;
    },

    setButtonClass() {
      return !this.isFormInvalid ? "archiveActive" : "archiveInactive";
    },

    additionalImages() {
      return this.getNewsfeedImages.filter((x) => !x.isTitle);
    },

    titleImages() {
      return this.getNewsfeedImages.filter((x) => x.isTitle);
    },
  },

  methods: {
    ...mapActions("Newsfeed", [
      "createPost",
      "setErr",
      "fetchAllAudiencesByUser",
      "fetchAllFacultiesByUser",
      "fetchAllCategories",
      "preparePost",
      "fetchNewsById",
      "updatePost",
      "fetchNewsfeedImages",
    ]),
    ...mapActions("places", [
      "fetchFilteredPlaces",
      "fetchPlacesByPageAndItemsPerPage",
    ]),
    ...mapMutations('Newsfeed', [
        'setImages',
    ]),

    onClickChild(obj) {
      if (obj.type === "faculty") {
        this.facultyIds = obj.value;
      }
      if (obj.type === "audience") {
        this.audienceIds = obj.value;
      }
      if (obj.type === "category") {
        this.categoryIds = obj.value;
      }
    },

    setTitleImages(titleImages) {
      titleImages.forEach(f => f.isTitle = true)
      this.setImages([
          ...titleImages,
          ...this.additionalImages,
      ])
    },

    setAdditionalImages(additionalImages) {
      additionalImages.forEach(f => f.isTitle = false)
      this.setImages([
        ...this.titleImages,
        ...additionalImages,
      ])
    },

    changeCzText(text) {
      this.markdownedTextCz = text;
      this.singleDetail.localizedAttributes.cz.text = text;
    },

    changeEnText(text) {
      this.markdownedTextEn = text;
      this.singleDetail.localizedAttributes.en.text = text;
    },

    removeItem(text, type, index) {
      if (window.confirm(`${this.$t("news.delete")} ${type} ${text} ?`)) {
        if (type === "odkaz") {
          this.singleDetail.links = this.singleDetail.links.filter(
            (element, i) => i !== index
          );
        } else {
          this.singleDetail.contacts = this.singleDetail.contacts.filter(
            (element, i) => i !== index
          );
        }
      }
    },

    openDialog(cardType) {
      if (cardType === "contact") this.showDialogContact = true;
      if (cardType === "link") this.showDialogLink = true;
      this.isNewCard = true;
    },

    closeDialog(cardType) {
      if (cardType === "contact") {
        this.showDialogContact = false;
      }
      if (cardType === "link") {
        this.showDialogLink = false;
      }
      this.itemCard = {};
    },

    prepareEditCard(text, index, cardType) {
      if (cardType === "contact") {
        this.itemCard = {title: text, index};
        this.showDialogContact = true;
      }
      if (cardType === "link") {
        this.itemCard = {title: text.replace(/^https?:\/\//i, ""), index};
        this.showDialogLink = true;
      }
      this.isNewCard = false;
    },
    updateCard(cardType, index, text){
      if(cardType === "contact"){
        this.singleDetail.contacts[index] = text
      }
      if(cardType === "link"){
        this.singleDetail.links[index] = text
      }
    },

    saveCard(text, index, isNew, cardType) {
      if (cardType === "contact") {
        isNew
          ? this.singleDetail.contacts.unshift(text)
          : this.updateCard(cardType, index, text);

      }
      if (cardType === "link") {
        isNew
          ? this.singleDetail.links.unshift(text)
          : this.updateCard(cardType, index, text);
      }
    },

    askAboutNotification() {
      if (
        !this.singleDetail.startDate &&
        !this.isNew &&
        !this.singleDetail.isConcept
      ) {
        this.showNotificationDialog = true;
      } else {
        this.savePost("publish", false);
      }
    },

    checkDateAndTime() {
      if (this.singleDetail.eventDate === null) {
        this.singleDetail.eventTime = null;
      }
    },

    onConfirmSave(notify) {
      this.showNotificationDialog = false;
      this.savePost("publish", notify);
    },

    async savePost(type, notify = false) {
      this.postUploading = true;
      this.setValues(type);
      this.checkDateAndTime();
      this.preparePlaceBeforeSaving()

      if (this.isNew) {
        const postId = await this.createPost(this.singleDetail);
        await this.uploadImages(postId);
      } else {
        await this.updatePost({
          id: this.newsId,
          request: this.singleDetail,
          notify: notify,
        });
        await this.uploadImages(this.newsId);
      }

      this.postUploading = false;

      // Check if the current route is already redirected before navigating
      if (this.$route.path.includes("/newsfeed/")) {
        await this.$router.push({name: "Newsfeed"});
        window.scrollTo(0, 0);
      }
    },

    setValues(type) {
      if (type === "publish") {
        this.singleDetail.isConcept = false;
        this.singleDetail.isArchived = false;
      }
      if (type === "archive") {
        this.singleDetail.isArchived = true;
        this.singleDetail.isConcept = false;
        this.savedArchived = true;
      }
      if (type === "concept") {
        this.savedConcept = true;
      }
      if (this.singleDetail.isPoster) {
        this.singleDetail.localizedAttributes.cz.text = null;
        this.singleDetail.localizedAttributes.en.text = null;
      }

      if (this.singleDetail.localizedAttributes.cz.text !== null) {
        this.singleDetail.localizedAttributes.cz.unformattedText =
          this.markdownToPlainText(
            this.singleDetail.localizedAttributes.cz.text
          );
      }

      if (this.singleDetail.localizedAttributes.en.text !== null) {
        this.singleDetail.localizedAttributes.en.unformattedText =
          this.markdownToPlainText(
            this.singleDetail.localizedAttributes.en.text
          );
      }

      this.singleDetail.facultyIds = this.facultyIds;
      this.singleDetail.audienceIds = this.audienceIds;
      this.singleDetail.categoryIds = this.categoryIds;
    },
    preparePlaceBeforeSaving() {
      if(this.place){
        // if user pick place from list, localizedAttributes must be empty.
        // otherwise we don't know if user set custom places or chose from list
        if(this.isPlaceFromList()){
          this.singleDetail.localizedAttributes.cz.customPlace = null;
          this.singleDetail.localizedAttributes.en.customPlace = null;
          this.singleDetail.placeId = this.place.id
        } else {
          this.singleDetail.localizedAttributes.cz.customPlace = this.place;
          this.singleDetail.localizedAttributes.en.customPlace = this.placeEN;
          this.singleDetail.placeId = null
        }
      } else {
        this.singleDetail.localizedAttributes.cz.customPlace = null;
        this.singleDetail.localizedAttributes.en.customPlace = null;
        this.singleDetail.placeId = null
      }
    },
    setPlaceAfterFetch(){
      if(this.singleDetail.place){
        this.singleDetail.placeId = this.singleDetail.place.id
      } else {
        this.singleDetail.placeId = null
      }
    },
    controlFacultyAccess() {
      this.hasFacultyAccess = this.getFacultiesByUser.find(
        (faculty) => faculty.id === this.facultyId
      );
    },
    isPlaceFromList() {
      return typeof this.place === "object"
    },
    fetchData() {
      if (this.isNew) {
        this.facultyIds = [this.facultyId];
      } else if (this.singleDetail) {
        this.facultyIds = this.singleDetail.faculty.map((obj) => obj.id);
        this.audienceIds = this.singleDetail.audience.map((obj) => obj.id);
        this.categoryIds = this.singleDetail.category.map((obj) => obj.id);
        this.title = this.singleDetail.localizedAttributes.cz.title;
        this.loadPlace();
      }
    },

    loadPlace() {
      if (this.singleDetail.place) {
        this.place = {
          title: this.singleDetail.place.localizedAttributes.cz.name,
          id: this.singleDetail.place.id,
        };
        this.singleDetail.placeId = this.singleDetail.place.id
      } else {
        this.place = this.singleDetail.localizedAttributes.cz.customPlace;
        this.placeEN = this.singleDetail.localizedAttributes.en.customPlace;
      }
    },

    loadPostStatus() {
      if (
        this.singleDetail &&
        !window.location.href.includes("#Text") &&
        !this.singleDetail.isPoster
      ) {
        this.$router.push("#Text");
      }
      if (
        this.singleDetail &&
        !window.location.href.includes("#Poster") &&
        this.singleDetail.isPoster
      ) {
        this.$router.push("#Poster");
      }
    },

    async fetchPlaces() {
      if(this.placeSearch !== ""){
        await this.fetchPlacesByPageAndItemsPerPage({
          searchString: this.placeSearch,
          language: "cz",
          buildingId: null,
          floorId: null,
          exterior: null,
          placeType: null,
          page: null,
          itemsPerPage: null,
        });
      }
    },

    updateDate(value) {
      this.singleDetail.eventDate = value ? value.format("YYYY-MM-DD") : null;
      this.checkDateAndTime();
    },

    finishSaving() {
      this.hasRecentlyUpdated = true;
      setTimeout(() => (this.hasRecentlyUpdated = false), 2000);
    },

    async uploadImages(id) {
      await this.$refs.titleImageManager.upload(
          `newsfeed/news/images/${id}`,
          { isTitle: true }
      )
      if (this.isTextual) {
        await this.$refs.additionalImagesManager.upload(
            `newsfeed/news/images/${id}`,
            {isTitle: false}
        )
      }
    },

    markdownToPlainText(markdown) {
      const md = new MarkdownIt();
      const html = md.render(markdown);

      const tempElement = document.createElement("div");
      tempElement.innerHTML = html;

      return tempElement.textContent || tempElement.innerText || "";
    },
  },

  watch: {
    tab() {
      this.singleDetail.isPoster = this.tab === "Poster";
    },

    placeSearch() {
      this.fetchPlaces();
    },

    place(newPlace){
      // if user picked from list, the custom english place name is empty
      if(typeof newPlace === "object"){
        this.placeEN = ""
      }
    }
  },
};
</script>

<style scoped>
.footerText {
  color: #6d7278;
}

.footerBackground {
  background-color: #f5f5f5;
}

.footerTextSuccess {
  color: #78be13;
}

.footerTextArchived {
  color: #d3661a;
}

.archiveActive {
  color: #e02020;
  font-size: 18px;
  font-weight: 500;
  cursor: pointer;
}

.archiveInactive {
  color: silver;
  font-size: 18px;
  font-weight: 500;
  cursor: default;
}

.photos-component {
  margin: 0;
  max-width: 35vw;
}

@media screen and (max-width: 600px) {
  .photos-component {
    margin: 0 auto;
    max-width: 100vw;
  }
}

@media screen and (min-width: 601px) {
  .photos-component {
    margin: 0 auto;
    max-width: 50vw;
  }
}
</style>
